import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Input,
  List,
  Button,
  Tag,
  Space,
  Typography,
  Skeleton,
  Empty,
  message,
} from 'antd';
import { StarOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_TEMPLATE_QUERY } from '@src/utils/sensor/track';
import { NEXA_QUERY_TEMPLATE_CLICK } from '../utils';
import useGetTokenUsage from '../hooks/useGetTokenUsage';
import { canSeeDeepModeFeatures } from '../utils/deepModeAccess';

const { Text } = Typography;

interface AnalysisItem {
  id: number;
  query: string;
  deep_mode: boolean;
  tags?: string[];
}

interface ViewAllDrawerProps {
  open: boolean;
  onClose: () => void;
  category: 'recommended' | 'my' | 'team';
  data: AnalysisItem[];
  isLoading: boolean;
}

const getCategoryConfig = (category: 'recommended' | 'my' | 'team') => {
  switch (category) {
    case 'recommended':
      return {
        title: 'Recommended Analyses',
        icon: <StarOutlined style={{ marginRight: 8 }} />,
      };
    case 'my':
      return {
        title: 'My Saved Analyses',
        icon: <UserOutlined style={{ marginRight: 8 }} />,
      };
    case 'team':
      return {
        title: 'Team Saved Analyses',
        icon: <TeamOutlined style={{ marginRight: 8 }} />,
      };
  }
};

const ViewAllDrawer: React.FC<ViewAllDrawerProps> = ({
  open,
  onClose,
  category,
  data,
  isLoading,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const { isOver } = useGetTokenUsage();
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const config = getCategoryConfig(category);

  // Filter data based on search text and selected tags
  const filteredData = useMemo(() => {
    let filtered = data;

    // Filter by search text (query + tags)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) => {
        const queryMatch = item.query.toLowerCase().includes(searchLower);
        const tagsMatch = item.tags?.some((tag) => tag.toLowerCase().includes(searchLower));
        return queryMatch || tagsMatch;
      });
    }

    // Filter by selected tags (match ANY)
    if (selectedFilterTags.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.tags || item.tags.length === 0) return false;
        return selectedFilterTags.some((filterTag) =>
          item.tags!.some((itemTag) => itemTag.toLowerCase() === filterTag.toLowerCase()),
        );
      });
    }

    return filtered;
  }, [data, searchText, selectedFilterTags]);

  // Get all unique tags from data for filter UI
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    data.forEach((item) => {
      if (item.tags) {
        item.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [data]);

  const handleToggleFilterTag = (tag: string) => {
    setSelectedFilterTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleClick = (item: AnalysisItem) => {
    if (isOver) {
      message.error('Usage limit reached. Please contact support to continue using Nexa.');
      return;
    }

    const detail: any = {
      query: item.query,
      analysis_id: item.id,
      deep_mode: item.deep_mode,
    };

    window.dispatchEvent(new CustomEvent(NEXA_QUERY_TEMPLATE_CLICK, { detail }));
    apptracker(NEXA_TEMPLATE_QUERY, { value: item.query, analysis_id: item.id });

    // Close drawer after selection
    onClose();
  };

  const handleCloseDrawer = () => {
    // Reset filters when closing
    setSearchText('');
    setSelectedFilterTags([]);
    onClose();
  };

  return (
    <Drawer
      title={
        <span>
          {config.icon}
          {config.title}
        </span>
      }
      placement="right"
      closable={true}
      onClose={handleCloseDrawer}
      open={open}
      width={600}
      destroyOnClose={true}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Search Input */}
        <Input
          placeholder="Search by query or tags..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16 }}
          allowClear
        />

        {/* Tag Filters */}
        {allTags.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
              Filter by tags:
            </Text>
            <Space size={[4, 4]} wrap>
              {allTags.map((tag) => (
                <Tag
                  key={tag}
                  color={selectedFilterTags.includes(tag) ? 'blue' : 'default'}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleToggleFilterTag(tag)}
                >
                  {tag}
                </Tag>
              ))}
            </Space>
            {selectedFilterTags.length > 0 && (
              <Button
                type="link"
                size="small"
                onClick={() => setSelectedFilterTags([])}
                style={{ padding: '4px 0', height: 'auto', marginTop: 8 }}
              >
                Clear tag filters
              </Button>
            )}
          </div>
        )}

        {/* Results Count */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {filteredData.length} {filteredData.length === 1 ? 'analysis' : 'analyses'}
          </Text>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : filteredData.length === 0 ? (
            <Empty description="No analyses found" />
          ) : (
            <List
              dataSource={filteredData}
              renderItem={(item) => (
                <List.Item
                  onClick={() => handleClick(item)}
                  style={{
                    cursor: 'pointer',
                    padding: '16px',
                    borderRadius: 4,
                    transition: 'background-color 0.2s',
                    marginBottom: 8,
                    border: '1px solid #f0f0f0',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{item.query}</Text>
                    </div>
                    {((canSeeDeepMode && item.deep_mode) ||
                      (item.tags && item.tags.length > 0)) && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {canSeeDeepMode && item.deep_mode && <Tag color="blue">Deep</Tag>}
                        {item.tags?.map((tag) => (
                          <Tag key={tag} color="default">
                            {tag}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ViewAllDrawer;
